'use client';

import { Button } from '~/components/ui/button';
import { Form } from '../ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { trpc } from '~/trpc/client';
import { FormInput } from '../forms/form-input';
import { FormSelect } from '../forms/form-select';
import {
  type CreditApplicationFormSchema,
  creditApplicationFormSchema,
} from '~/validation-schemas/credit-application-form';
import { toast } from 'sonner';
import { isTRPCClientError } from '~/trpc/is-trpc-error';
import { cn } from '~/lib/utils';
import { type CallToActionButtonStyleReturn } from './call-to-action';

interface BlockCreditApplicationFormProps {
  className?: string;
  customButton: CallToActionButtonStyleReturn;
  formIdentifier: string;
  pageUrl: string;
}

const establishmentTypeOptions = [
  { value: 'charity', label: 'Charity' },
  { value: 'limited company', label: 'Limited Company' },
  {
    value: 'public sector funded organisation',
    label: 'Public Sector Funded Organisation',
  },
];

export function BlockCreditApplicationForm({
  className,
  customButton,
  formIdentifier,
  pageUrl,
}: Readonly<BlockCreditApplicationFormProps>) {
  const submitCreditApplicationForm =
    trpc.formRouter.submitCreditApplicationForm.useMutation();

  const form = useForm<CreditApplicationFormSchema>({
    resolver: zodResolver(creditApplicationFormSchema),
    defaultValues: {
      firstName: '',
      surname: '',
      phone: '',
      email: '',
      accountNo: '',
      company: '',
      establishmentType: '',
      establishmentName: '',
      city: '',
      postcode: '',
    },
  });

  const onSubmit = async (data: CreditApplicationFormSchema) => {
    if (submitCreditApplicationForm.isPending) return;
    try {
      await toast
        .promise(
          submitCreditApplicationForm.mutateAsync({
            ...data,
            formIdentifier,
            pageUrl,
          }),
          {
            loading: 'Submitting credit application...',
            success: () => {
              return `Credit application submitted successfully`;
            },
            error: (error) => {
              if (isTRPCClientError(error)) {
                return error.message;
              }
              return `Error submitting credit application`;
            },
          },
        )
        .unwrap();
      form.reset();
    } catch (error) {
      console.error('Error submitting credit application:', error);
    }
  };

  const isDisabled = form.formState.isSubmitting;

  return (
    <div className={cn('w-full sm:mx-auto sm:max-w-3xl', className)}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-4 px-2 sm:grid-cols-2">
            <div className="col-span-1">
              <FormInput
                id="firstName"
                name="firstName"
                label="First Name"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your first name"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="surname"
                name="surname"
                label="Surname"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your surname"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="phone"
                name="phone"
                label="Phone Number"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your phone number"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="email"
                name="email"
                label="Email Address"
                type="email"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your email address"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="accountNo"
                name="accountNo"
                label="Account Number (Optional)"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your account number"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="company"
                name="company"
                label="Company"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your company name"
              />
            </div>
            <div className="col-span-1">
              <FormSelect
                key={String(form.watch('establishmentType'))}
                id="establishmentType"
                name="establishmentType"
                label="Establishment Type"
                control={form.control}
                disabled={isDisabled}
                placeholder="Select establishment type"
                options={establishmentTypeOptions}
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="establishmentName"
                name="establishmentName"
                label="Establishment Name"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter establishment name"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="city"
                name="city"
                label="City"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your city"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="postcode"
                name="postcode"
                label="Postcode"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your postcode"
              />
            </div>
            <div className="col-span-1 mt-2 sm:col-span-2">
              <Button
                type="submit"
                disabled={isDisabled}
                style={customButton?.customButtonStyle}
                className={cn(
                  'mx-auto block w-full sm:w-1/2',
                  customButton?.customButtonClasses,
                )}
                variant={customButton ? 'custom' : 'default'}
              >
                Submit Credit Application
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
